import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye, faTimes, faFileAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { TEST_SERVER_FILE_NOTE } from '../constants/uploadMessages';

const getExtension = (nameOrUrl = '') => {
  const clean = String(nameOrUrl).split('?')[0].split('#')[0];
  const parts = clean.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isImageExt = (ext) => ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
const isPdfExt = (ext) => ext === 'pdf';

export async function downloadDocumentFile(url, filename = 'document') {
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function DocumentPreviewModal({ open, onClose, fileUrl, fileName }) {
  const [loadState, setLoadState] = useState('checking'); // checking | ok | missing
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open || !fileUrl) return undefined;
    let cancelled = false;
    setLoadState('checking');
    setErrorMessage('');

    (async () => {
      try {
        const res = await fetch(fileUrl, { method: 'HEAD' });
        if (cancelled) return;
        if (res.ok) {
          setLoadState('ok');
          return;
        }
        // Some hosts block HEAD — try a tiny GET range / full GET
        const getRes = await fetch(fileUrl, { method: 'GET' });
        if (cancelled) return;
        if (getRes.ok) {
          const type = (getRes.headers.get('content-type') || '').toLowerCase();
          if (type.includes('application/json')) {
            setErrorMessage(TEST_SERVER_FILE_NOTE);
            setLoadState('missing');
          } else {
            setLoadState('ok');
          }
        } else {
          setErrorMessage(TEST_SERVER_FILE_NOTE);
          setLoadState('missing');
        }
      } catch {
        if (!cancelled) {
          setErrorMessage(TEST_SERVER_FILE_NOTE);
          setLoadState('missing');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [open, fileUrl]);

  if (!open || !fileUrl) return null;

  const ext = getExtension(fileName) || getExtension(fileUrl);
  const canImage = isImageExt(ext);
  const canPdf = isPdfExt(ext);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10050,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      role="presentation"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Document preview"
        style={{
          background: '#fff', borderRadius: '14px', width: 'min(960px, 100%)',
          maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>Document Preview</div>
            <div style={{
              fontSize: '12px', color: '#6b7280', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70vw',
            }}>
              {fileName || 'Document'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            {loadState === 'ok' && (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => downloadDocumentFile(fileUrl, fileName)}
                title="Download"
              >
                <FontAwesomeIcon icon={faDownload} /> Download
              </button>
            )}
            <button
              type="button"
              className="btn-icon"
              onClick={onClose}
              title="Close"
              style={{ width: '34px', height: '34px' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div style={{
          flex: 1, minHeight: '320px', maxHeight: 'calc(92vh - 64px)',
          background: '#f8fafc', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: canImage || canPdf ? 0 : '32px',
          overflow: 'auto',
        }}>
          {loadState === 'checking' && (
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading preview...</p>
          )}

          {loadState === 'missing' && (
            <div style={{ textAlign: 'center', maxWidth: '480px', padding: '32px 24px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '42px', color: '#f59e0b', marginBottom: '12px' }} />
              <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#374151' }}>
                File is no longer available
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
                {errorMessage || TEST_SERVER_FILE_NOTE}
              </p>
            </div>
          )}

          {loadState === 'ok' && canImage && (
            <img
              src={fileUrl}
              alt={fileName || 'Preview'}
              style={{ maxWidth: '100%', maxHeight: 'calc(92vh - 80px)', objectFit: 'contain' }}
            />
          )}

          {loadState === 'ok' && canPdf && (
            <iframe
              title={fileName || 'PDF preview'}
              src={fileUrl}
              style={{ width: '100%', height: 'calc(92vh - 64px)', border: 'none' }}
            />
          )}

          {loadState === 'ok' && !canImage && !canPdf && (
            <div style={{ textAlign: 'center', maxWidth: '420px', padding: '24px' }}>
              <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '42px', color: '#9ca3af', marginBottom: '12px' }} />
              <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#374151' }}>
                Preview not available for this file type
              </p>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
                You can download the file to open it on your device.
              </p>
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => downloadDocumentFile(fileUrl, fileName)}
              >
                <FontAwesomeIcon icon={faDownload} /> Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Preview + Download actions for an uploaded document.
 */
export default function DocumentFileActions({
  fileUrl,
  fileName = 'document',
  showLabels = false,
  buttonStyle = {},
}) {
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!fileUrl) return null;

  const btnBase = {
    fontSize: '11px',
    padding: showLabels ? '4px 10px' : '4px 8px',
    ...buttonStyle,
  };

  return (
    <>
      <button
        type="button"
        className="btn-secondary btn-sm keep-visible"
        style={btnBase}
        title="Preview"
        onClick={() => setPreviewOpen(true)}
      >
        <FontAwesomeIcon icon={faEye} />
        {showLabels ? ' Preview' : null}
      </button>
      <button
        type="button"
        className="btn-secondary btn-sm keep-visible"
        style={btnBase}
        title="Download"
        onClick={() => downloadDocumentFile(fileUrl, fileName)}
      >
        <FontAwesomeIcon icon={faDownload} />
        {showLabels ? ' Download' : null}
      </button>
      <DocumentPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={fileUrl}
        fileName={fileName}
      />
    </>
  );
}
